import {
  gql,
  type OperationVariables,
  useMutation,
  useQuery,
} from '@apollo/client';
import {
  AffiliationRelations,
  AffiliationTypes,
  EmploymentStatus,
  FeatureFlagTypes,
  InvitationStatus,
  OrganizationUserAccessTypes,
  OrganizationUserEntityRelationTypes,
} from '@generated/graphql';
import type { Account, CustodianAffiliate } from '@interfaces/account';
import { isRESPAccount } from '@interfaces/account';
import type { Affiliation } from '@interfaces/affiliation';
import type { ForeignTaxInformation } from '@interfaces/foreignTaxInformation';
import type { PhysicalAddress } from '@interfaces/physicalAddress';
import type { User } from '@interfaces/user';
import { Gender } from '@interfaces/user';
import { AccountTypes } from '@onevesthq/ov-enums';
import { AddressField } from '@ovComponents/2-component/addressField/addressField';
import { CheckboxField } from '@ovComponents/2-component/checkboxField/checkboxField';
import { ClientSelectField } from '@ovComponents/2-component/clientSelectField/clientSelect';
import { DateField } from '@ovComponents/2-component/dateField/dateField';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { Form } from '@ovComponents/2-component/form/form';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { Radio, RadioGroup } from '@ovComponents/2-component/radio/radio';
import type { SegmentValue } from '@ovComponents/2-component/segmentedControl/segmentedControl';
import { SegmentedControl } from '@ovComponents/2-component/segmentedControl/segmentedControl';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { CitizenshipField } from '@ovComponents/3-pattern/citizenshipField/citizenshipField';
import { ForeignTaxField } from '@ovComponents/3-pattern/foreignTaxField/foreignTaxField';
import { NumberField } from '@ovComponents/3-pattern/numberField/numberField';
import { PhoneNumberField } from '@ovComponents/3-pattern/phoneNumberField/phoneNumberField';
import { SelectionTile } from '@ovComponents/3-pattern/selectionTile/selectionTile';
import { ADD_ENTITY_TO_ORG_USER } from '@ovComponents/4-module/widgets/accessToEntity/accessToEntity.queries';
import { UserContext } from '@providers/userContextProvider';
import { scaledDelay } from '@utils/appUtils';
import { phoneValidator } from '@utils/common';
import { ADVISOR_ENTITIES_COUNT_DELAY_THRESHOLD } from '@utils/constants';
import { enumValues } from '@utils/enumValues';
import { FlagsmithFlags } from '@utils/flagsmith';
import dayjs from 'dayjs';
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  Typography,
  useToast,
} from 'hephaestus/web';
import { Close } from 'hephaestus/webIcons';
import type { CountryCode } from 'libphonenumber-js';
import { cloneDeep, flatten, groupBy, isEqual, kebabCase } from 'lodash-es';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureFlag } from '../../../../../../hooks/useFeatureFlag';
import { useLocalization } from '../../../../../../hooks/useLocalization';
import { useOrganizationFeatureFlag } from '../../../../../../hooks/useOrganizationFeatureFlag';
import {
  type AccountConfigSection,
  ALL_RELATIONS,
  type FieldType,
  type FieldTypeOption,
} from '../accountConfig';
import { useFetchAccount } from '../editAccount.fetchAccount';
import type { EditAccountSubstepOptions } from '../editAccount.visual';
import type {
  AffiliateModalProps,
  InitiatedAffiliate,
} from './affiliateModal.types';
import {
  type AffiliationUpdateData,
  UPDATE_AFFILIATIONS,
} from './affiliateMutations';
import { SEARCH_CLIENTS_FOR_AFFILIATES } from './affiliateOperations.queries';
import { createUseRelatedAffiliationSelector } from './affiliateRelatedAffiliateSelector.factory';
import { CREATE_ORGANIZATION_USER, FETCH_ROLES } from './convertAffiliate';
import { TaxIdInputs } from './taxIdExists';

const MAXIMUM_ALLOCATION_PERCENTAGE = 100;

const IGNORED_AFFILIATE_USER_DIFF_KEYS = new Set([
  '__typename',
  'accessibleBy',
  'taxIdExists',
  'sinExists',
  'affiliateOnly',
]);

/** Keys where `from` / `to` differ vs snapshot taken when the modal opened. */
function diffAffiliateUserSinceSnapshot(
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown> | null | undefined,
): Record<string, { from: unknown; to: unknown }> {
  const diff: Record<string, { from: unknown; to: unknown }> = {};
  const keyList = Array.from(
    new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]),
  );
  for (let i = 0; i < keyList.length; i++) {
    const key = keyList[i];
    if (IGNORED_AFFILIATE_USER_DIFF_KEYS.has(key)) continue;
    const a = before?.[key];
    const b = after?.[key];
    if (!isEqual(a, b)) {
      diff[key] = { from: a, to: b };
    }
  }
  return diff;
}

const EMPLOYED_EMPLOYMENT_STATUSES = [
  EmploymentStatus.Retired,
  EmploymentStatus.Employed,
  EmploymentStatus.SelfEmployed,
];

const CREATE_AFFILIATE = gql`
  mutation createAffiliate($input: CreateAffiliateInput!) {
    createAffiliate(input: $input) {
      user {
        id
      }
    }
  }
`;

const CREATE_USER = gql`
  mutation createUser($input: CreateUserInput!) {
    createUser(input: $input) {
      user {
        id
      }
    }
  }
`;

const UPDATE_USER = gql`
  mutation updateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      user {
        id
        affiliateOnly
        accessibleBy(input: { excludeRepCodeUsers: true }) {
          relation
          organizationUser {
            id
          }
        }
      }
    }
  }
`;

export const FETCH_ALL_ACCESSIBLE_ACCOUNT_AFFILIATES = gql`
  query fetchAccounts($input: FetchAccountsInput!) {
    fetchAccounts(input: $input) {
      accounts {
        id: stringId
        affiliations {
          type
          user {
            id: stringId
            firstName
            lastName
            entityName
            primaryEmail
            affiliateOnly
          }
        }
      }
    }
  }
`;

type DeepPartial<T> = T extends object
  ? { [P in keyof T]?: DeepPartial<T[P]> }
  : T;

export const AffiliateModal = (props: {
  open: boolean;
  setOpen: (open: boolean) => void;
  affiliate?: Affiliation;
  action?: 'create' | 'edit';
  fields?: FieldType[];
  optionalFields?: FieldType[];
  type: AffiliationTypes;
  title?: string;
  accountId: string;
  affiliates: Affiliation[];
  refetch?: () => void;
  allAffiliates?: (Affiliation | CustodianAffiliate)[];
  account: Partial<Account>;
  useAccountHoldersAddress?: boolean;
  canUseExistingAffiliate?: boolean;
  options?: EditAccountSubstepOptions;
  onJointAffiliateCreation?: (initiatedAffiliate: InitiatedAffiliate) => void;
  isCustomJointAffiliateFlowUsed?: boolean;
  affiliateSections: AccountConfigSection[];
  onAffiliationsUpdated?: (args: {
    updateData?: AffiliationUpdateData | null;
    userId?: string;
    type: AffiliationTypes;
  }) => any;
  isWidget?: boolean;
}) => {
  const { translateBackendString } = useLocalization();
  const {
    open,
    setOpen,
    affiliate,
    action = 'create',
    fields = [],
    optionalFields = [],
    type,
    title,
    accountId,
    affiliates,
    allAffiliates,
    account,
    options,
    onJointAffiliateCreation,
    isCustomJointAffiliateFlowUsed,
    affiliateSections,
    onAffiliationsUpdated,
  } = props;
  let { useAccountHoldersAddress } = props;
  const { t } = useTranslation(['affiliationTypes', 'client']);
  const { activeOrganization, userContext } = useContext(UserContext);
  const accountHolderEntityAccess = userContext.entities?.find(
    (entityAccess) => entityAccess.entity.id === account?.user?.id,
  );
  const isAdvisorDriven =
    !!account?.user?.id &&
    accountHolderEntityAccess?.relation !==
      OrganizationUserEntityRelationTypes.Owner;
  const creatorRelationToAffiliate =
    accountHolderEntityAccess?.relation ??
    OrganizationUserEntityRelationTypes.Other;

  type AffiliateDataState = DeepPartial<Affiliation> & {
    roleId?: string;
    organizationId?: string;
    user: Partial<User>;
  };
  const [affiliateData, setAffiliateData] = useState<AffiliateDataState>(
    affiliate as AffiliateDataState,
  );
  const [focused, setFocused] = useState<
    Array<
      | keyof typeof affiliateData.user
      | keyof typeof affiliateData.relation
      | keyof typeof affiliateData
      | FieldTypeOption
    >
  >([]);
  const [checkUseExistingAffiliate, setCheckUseExistingAffiliate] =
    useState(false);
  const [checkUseAccountOwner, setCheckUseAccountOwner] = useState(false);
  const [existingUserSelected, setExistingUserSelected] = useState(false);
  const initialAffiliateUserSnapshotRef = useRef<Record<
    string,
    unknown
  > | null>(null);
  const [grantAction, setGrantAction] = useState<SegmentValue>('primary');
  const [createAffiliate, { loading: createAffiliateLoading }] =
    useMutation(CREATE_AFFILIATE);
  const [createUser, { loading: createUserLoading }] = useMutation(CREATE_USER);
  const [updateUser, { loading: updateLoading }] = useMutation(UPDATE_USER);
  const [updateAffiliationsMutation, { loading }] =
    useMutation<AffiliationUpdateData>(UPDATE_AFFILIATIONS);
  const isAutoInviteEnabledInFlagsmith = useFeatureFlag(
    FlagsmithFlags.auto_invite,
  );
  const isAutoInviteSettingsEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.AutoInvite,
  );
  const autoInviteEnabled: boolean =
    isAutoInviteEnabledInFlagsmith && isAutoInviteSettingsEnabled;

  const updateAffiliations = async (
    args: Parameters<typeof updateAffiliationsMutation>[0] & {
      userId?: string;
    },
  ) => {
    const { data: affiliationsUpdateData } =
      await updateAffiliationsMutation(args);
    if (!affiliationsUpdateData?.updateAffiliations) {
      return affiliationsUpdateData;
    }
    const incompleteAffiliations =
      affiliationsUpdateData.updateAffiliations.incompleteAffiliations ?? [];
    if (incompleteAffiliations.length > 0) {
      const currentAffiliateIncompleteFields = incompleteAffiliations[0]
        ?.incompleteFields as typeof focused;
      setFocused(currentAffiliateIncompleteFields);
    } else {
      onAffiliationsUpdated?.({
        updateData: affiliationsUpdateData,
        userId: args.userId,
        type,
      });
      setOpen(false);
    }
    return affiliationsUpdateData;
  };
  const [createOrganizationUser] = useMutation(CREATE_ORGANIZATION_USER);
  const [addEntityToOrganizationUser] = useMutation(ADD_ENTITY_TO_ORG_USER);
  const [disableEditForm, setDisableEditForm] = useState(false);
  const [countryCode, setCountryCode] = useState<CountryCode | undefined>(
    undefined,
  );
  const { enqueueToast } = useToast();

  let requiredFields = fields.map((field) => field.type);
  let allFieldTypes = requiredFields.concat(
    ...optionalFields.map((field) => field.type),
  );
  if (isCustomJointAffiliateFlowUsed) {
    const jointInitialFields = ['fullName', 'relation'];
    allFieldTypes = allFieldTypes.filter(
      (availableField?: string) =>
        availableField && jointInitialFields.includes(availableField),
    );
    requiredFields = requiredFields.filter(
      (availableField?: string) =>
        availableField && jointInitialFields.includes(availableField),
    );
    useAccountHoldersAddress = false;
  }
  const relations = fields.find((field) => field?.type === 'relation')?.options;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isDisabled =
    createAffiliateLoading ||
    updateLoading ||
    createUserLoading ||
    loading ||
    isSubmitting;
  const isUsingExistingAffiliate =
    (action === 'edit' &&
      (allAffiliates as Affiliation[])?.some(
        (a) => a.user?.id === affiliate?.user.id && a.id !== affiliate?.id,
      )) ||
    (action === 'create' &&
      (allAffiliates as Affiliation[])?.some(
        (a) => a.user?.id === affiliateData.user.id,
      ));
  const isUsingAccountOwner = affiliateData.user.id === account?.user?.id;
  const selectExistingUsersEnabled =
    action === 'create' && (options?.selectExistingUsers ?? false);
  const createOrReturnEntityUsersOnly =
    [AffiliationTypes.AuthorizedIndividual, AffiliationTypes.Joint].includes(
      type,
    ) && options?.createFullUsers;

  const updateAffiliateData = (updatedAffiliate: any) => {
    setAffiliateData(updatedAffiliate);
    setIsSubmitting(false);
  };

  if (createOrReturnEntityUsersOnly && action === 'create') {
    if (!allFieldTypes.includes('primaryEmail')) {
      allFieldTypes.push('primaryEmail');
    }
    if (!allFieldTypes.includes('roleId')) {
      allFieldTypes.push('roleId');
    }
  }

  const { loading: rolesLoading, data } = useQuery(FETCH_ROLES, {
    variables: {
      input: {
        filter: { organizationId: activeOrganization.id || undefined },
        pagination: { perPage: 50 },
      },
    },
    skip: !createOrReturnEntityUsersOnly,
  });

  const { data: allAffiliateData } = useQuery(
    FETCH_ALL_ACCESSIBLE_ACCOUNT_AFFILIATES,
    {
      variables: {
        input: {
          filter: { userId: account?.user?.id },
          pagination: { perPage: 100 },
        },
      },
      skip: !account?.user?.id,
    },
  );

  const allAffiliatesForUser =
    flatten(
      allAffiliateData?.fetchAccounts?.accounts.map(
        (acc: any) => acc.affiliations,
      ),
    ) ?? account?.affiliations;

  const linkAccountHolderAsOwnerOfEntity = async (entityId: string) => {
    const accountHolderOwner = account?.user?.accessibleBy?.find(
      (accessibleUser) =>
        accessibleUser?.relation === OrganizationUserEntityRelationTypes.Owner,
    );
    const organizationUserId = accountHolderOwner?.organizationUser?.id;
    if (!organizationUserId) return;

    await addEntityToOrganizationUser({
      variables: {
        input: {
          organizationUserId,
          entityId,
          readOnly: false,
          relation: OrganizationUserEntityRelationTypes.Owner,
        },
      },
    });
  };

  const createAffiliateOrFullUser = async ({
    variables,
    userData,
    updatedAttributes,
  }: {
    variables: OperationVariables;
    userData: any;
    updatedAttributes?: string[];
  }) => {
    const create = createOrReturnEntityUsersOnly ? createUser : createAffiliate;

    const createVariables = isAdvisorDriven
      ? {
          ...variables,
          input: {
            ...variables.input,
            relationToCreator: creatorRelationToAffiliate,
          },
        }
      : variables;

    const { data: res }: any = await create({ variables: createVariables });

    let createdUser: any;

    if (createOrReturnEntityUsersOnly) {
      createdUser = res?.createUser.user;
    } else {
      createdUser = res?.createAffiliate.user;
    }

    await scaledDelay({
      magnitude: userContext.entitiesCount ?? 0,
      threshold: ADVISOR_ENTITIES_COUNT_DELAY_THRESHOLD,
    });
    if (isCustomJointAffiliateFlowUsed && onJointAffiliateCreation) {
      onJointAffiliateCreation({
        id: createdUser.id,
        firstName: userData?.firstName ?? '',
        lastName: userData?.lastName ?? '',
        relation: affiliateData.relation as AffiliationRelations,
      });
      handleClose();
    } else {
      await updateAccountAffiliations(createdUser.id, updatedAttributes);
    }

    if (createOrReturnEntityUsersOnly) {
      await scaledDelay({
        magnitude: userContext.entitiesCount ?? 0,
        threshold: ADVISOR_ENTITIES_COUNT_DELAY_THRESHOLD,
      });
      await createOrganizationUser({
        variables: {
          input: {
            firstName: userData?.firstName,
            lastName: userData?.lastName,
            email: userData?.primaryEmail,
            organizationId: activeOrganization.id,
            roleId: affiliateData.roleId,
            autoInviteUser: true,
            accessType: OrganizationUserAccessTypes.Entity,
            entities: [
              {
                readOnly: false,
                entityId: createdUser.id,
                relation: OrganizationUserEntityRelationTypes.Owner,
              },
              ...(type === AffiliationTypes.AuthorizedIndividual
                ? [
                    {
                      readOnly: false,
                      entityId: account?.user?.id,
                      relation: OrganizationUserEntityRelationTypes.Other,
                    },
                  ]
                : []),
            ],
          },
        },
      });
    }

    if (isAdvisorDriven) {
      await linkAccountHolderAsOwnerOfEntity(createdUser.id);
    }
  };

  useEffect(() => {
    if (!open) return;
    const shouldRecalculateAllocation =
      affiliate?.user &&
      Object.keys(affiliate?.user).length === 0 &&
      allFieldTypes.includes('allocated');
    if (shouldRecalculateAllocation) {
      const allocation = requiredFields.includes('allocated')
        ? calculateAllocation()
        : 0;
      updateAffiliateData({ ...affiliate, allocation });
    } else {
      updateAffiliateData({ ...affiliate, allocation: undefined });
    }
    setFocused([]);
  }, [affiliate, open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    initialAffiliateUserSnapshotRef.current = cloneDeep(
      affiliate?.user ?? {},
    ) as Record<string, unknown>;
  }, [open, affiliate]);

  const calculateAllocation = () => {
    const existingAffiliates = affiliates.filter((x: any) => x.type === type);
    if (existingAffiliates.length === 0) return MAXIMUM_ALLOCATION_PERCENTAGE;

    // Calculate the sum of existing allocations
    const existingAllocationsSum = existingAffiliates.reduce(
      (sum, aff) => sum + (aff.allocation || 0),
      0,
    );

    // Return the remainder
    return Math.max(0, MAXIMUM_ALLOCATION_PERCENTAGE - existingAllocationsSum);
  };

  const getUpdatedAttributesInput = (updatedAttributes?: string[]) =>
    updatedAttributes && updatedAttributes.length > 0
      ? { updatedAttributes }
      : {};

  const updateAccountAffiliations = async (
    userId: string,
    updatedAttributes?: string[],
  ) => {
    let allocation;

    if (requiredFields.includes('allocation')) {
      allocation = calculateAllocation();
    } else if (allFieldTypes.includes('allocated')) {
      allocation = affiliateData.allocation || 0;
    }

    const { relatedAffiliations, incompleteAffiliations } =
      await getRelatedAffiliations();
    const affiliations = [
      ...(allAffiliates || []).map((item: any) => ({
        id: item.id,
        allocation: item.allocation,
        relation: item.relation,
        type: item.type,
        userId: item.user.id,
        relatedAffiliations: item.relatedAffiliations,
        invitationStatus: item?.invitationStatus,
      })),
      ...relatedAffiliations.filter(
        (aff) =>
          !((allAffiliates || []) as Affiliation[]).some(
            (_aff) => aff.id === _aff.id,
          ),
      ),
      {
        userId,
        relation: affiliateData.relation || AffiliationRelations.Other,
        type,
        allocation: type === AffiliationTypes.Joint ? undefined : allocation,
        relatedAffiliations: relatedAffiliations.map((a) => a?.id),
        invitationStatus:
          type === AffiliationTypes.Joint && autoInviteEnabled
            ? InvitationStatus.Pending
            : undefined,
      },
    ];

    if (incompleteAffiliations.length) {
      enqueueToast(
        t('incompleteAffiliates', {
          affiliationTypes: incompleteAffiliations
            .map((aff) => t(aff.type))
            .join(', '),
        }),
        { variant: 'error' },
      );
    }

    await updateAffiliations({
      variables: {
        input: {
          accountId,
          affiliations,
          sendInvitation:
            autoInviteEnabled && props?.isWidget && options?.enableAutoInvite
              ? true
              : undefined,
          ...getUpdatedAttributesInput(updatedAttributes),
        },
      },
      userId,
    });
  };

  const updateExistingAccountAffiliations = async (
    updatedAttributes?: string[],
  ) => {
    const { relatedAffiliations, incompleteAffiliations } =
      await getRelatedAffiliations();
    const existingAffiliations = allAffiliates as Affiliation[];
    const affiliations = [
      ...existingAffiliations.map((item) =>
        item.id === affiliateData.id
          ? {
              id: item.id,
              type,
              userId: affiliateData.user.id,
              relation: affiliateData?.relation ?? AffiliationRelations.Other,
              allocation: affiliateData?.allocation ?? affiliate?.allocation,
              relatedAffiliations: relatedAffiliations.map((a) => a?.id),
            }
          : {
              id: item.id,
              allocation: item.allocation,
              relation: item.relation,
              type: item.type,
              userId: item.user.id,
              relatedAffiliations: item.relatedAffiliations,
            },
      ),
      ...relatedAffiliations.filter(
        (aff) => !existingAffiliations.some((_aff) => aff.id === _aff.id),
      ),
    ];

    if (incompleteAffiliations.length) {
      enqueueToast(
        t('incompleteAffiliates', {
          affiliationTypes: incompleteAffiliations
            .map((aff) => t(aff.type))
            .join(', '),
        }),
        { variant: 'error' },
      );
    }
    await updateAffiliations({
      variables: {
        input: {
          accountId,
          affiliations,
          ...getUpdatedAttributesInput(updatedAttributes),
        },
      },
      userId: affiliateData.user.id,
    });
  };

  const handleClose = () => {
    (document.activeElement as HTMLElement | null)?.blur();
    setOpen(false);
    setCheckUseExistingAffiliate(false);
    setCheckUseAccountOwner(false);
    setExistingUserSelected(false);
    setGrantAction('primary');
    setIsSubmitting(false);
  };

  const clearUserObject = (userDataObject: any) => {
    const clonedUserDataObject = cloneDeep(userDataObject) ?? {};
    const {
      id: _id,
      taxIdExists: _taxIdExists,
      __typename: _typename,
      sinExists: _sinExists,
      affiliateOnly: _affiliateOnly,
      foreignTaxInformation,
      physicalAddress,
      employmentStatus,
      companyType,
      jobTitle,
      maritalStatus,
      citizenships,
      ...rest
    } = clonedUserDataObject;

    const cleanedPhysicalAddress = physicalAddress
      ? (() => {
          const {
            __typename: _addressTypename,
            province,
            ...addressRest
          } = physicalAddress;
          // For USA addresses, province can be null from backend and breaks updates.
          return province === null ? addressRest : { ...addressRest, province };
        })()
      : undefined;

    const cleanedForeignTaxInformation = Array.isArray(foreignTaxInformation)
      ? foreignTaxInformation.map((item: any) => {
          const { __typename: _taxInfoTypename, ...taxInfoRest } = item ?? {};
          return taxInfoRest;
        })
      : undefined;

    return {
      ...rest,
      ...(employmentStatus ? { employmentStatus } : {}),
      ...(companyType ? { companyType } : {}),
      ...(jobTitle ? { jobTitle } : {}),
      ...(maritalStatus ? { maritalStatus } : {}),
      ...(Array.isArray(citizenships) && citizenships.length > 0
        ? { citizenships }
        : {}),
      ...(cleanedPhysicalAddress
        ? { physicalAddress: cleanedPhysicalAddress }
        : {}),
      ...(cleanedForeignTaxInformation
        ? { foreignTaxInformation: cleanedForeignTaxInformation }
        : {}),
    };
  };

  const submit = async () => {
    setIsSubmitting(true);
    try {
      if (disableEditForm && affiliateData?.user?.id) {
        await updateAccountAffiliations(affiliateData.user.id as string);
        setIsSubmitting(false);
        return;
      }
      if (!validate()) {
        setIsSubmitting(false);
        return;
      }
      const userFieldChangesSinceOpen = diffAffiliateUserSinceSnapshot(
        initialAffiliateUserSnapshotRef.current,
        affiliateData.user as Record<string, unknown>,
      );
      const updatedAttributes = Object.keys(userFieldChangesSinceOpen);

      const userData = {
        ...affiliateData.user,
        isOfficerOfPublicCompany: allFieldTypes.includes(
          'isOfficerOfPublicCompany',
        )
          ? (affiliateData.user?.isOfficerOfPublicCompany ?? false)
          : undefined,
        isOwnerOfPublicCompany: allFieldTypes.includes('isOwnerOfPublicCompany')
          ? (affiliateData.user?.isOwnerOfPublicCompany ?? false)
          : undefined,
        isMemberOfIiroc: allFieldTypes.includes('isMemberOfIiroc')
          ? (affiliateData.user?.isMemberOfIiroc ?? false)
          : undefined,
        politicallyExposedForeignPerson: allFieldTypes.includes(
          'politicallyExposedForeignPerson',
        )
          ? (affiliateData.user?.politicallyExposedForeignPerson ?? false)
          : undefined,
        politicallyExposedDomesticPerson: allFieldTypes.includes(
          'politicallyExposedDomesticPerson',
        )
          ? (affiliateData.user?.politicallyExposedDomesticPerson ?? false)
          : undefined,
        closeAssociateOfPEP: allFieldTypes.includes('closeAssociateOfPEP')
          ? (affiliateData.user?.closeAssociateOfPEP ?? false)
          : undefined,
        headOfInternationalOrganization: allFieldTypes.includes(
          'headOfInternationalOrganization',
        )
          ? (affiliateData.user?.headOfInternationalOrganization ?? false)
          : undefined,
        foreignTaxInformation: allFieldTypes.includes('foreignTaxInformation')
          ? (affiliateData.user?.foreignTaxInformation ?? [])
          : undefined,
        phone: allFieldTypes.includes('phone')
          ? (affiliateData.user?.phone ?? undefined)
          : undefined,
        accessibleBy: undefined,
      };
      if (
        action === 'create' &&
        !checkUseExistingAffiliate &&
        action === 'create' &&
        !checkUseAccountOwner
      ) {
        if (!userData?.dateOfBirth) delete userData?.dateOfBirth;
        if (!userData?.dateOfDeath) delete userData?.dateOfDeath;

        if (existingUserSelected) {
          const cleanedUserData = clearUserObject(userData);
          const input = { userId: affiliateData.user.id, ...cleanedUserData };
          const { data: updatedUserData } = await updateUser({
            variables: { input },
          });
          await updateAccountAffiliations(
            affiliateData.user.id as string,
            updatedAttributes,
          );

          const isFullUser =
            updatedUserData?.updateUser?.user &&
            !updatedUserData.updateUser.user.affiliateOnly;
          if (!isFullUser) {
            return;
          }

          const existingOrgUserOwner =
            updatedUserData.updateUser.user.accessibleBy.find(
              (orgUser: any) =>
                orgUser.relation === OrganizationUserEntityRelationTypes.Owner,
            );
          const orgUserDoesNotHaveAccess = !account?.user?.accessibleBy?.some(
            (accessibleUser) =>
              accessibleUser?.organizationUser?.id ===
              existingOrgUserOwner.organizationUser.id,
          );
          const updatedUserShouldBeInvitedToNIE =
            [AffiliationTypes.AuthorizedIndividual].includes(type) &&
            orgUserDoesNotHaveAccess;

          if (updatedUserShouldBeInvitedToNIE) {
            await addEntityToOrganizationUser({
              variables: {
                input: {
                  entityId: account?.user?.id,
                  organizationUserId: existingOrgUserOwner.organizationUser.id,
                  readOnly: false,
                  relation: OrganizationUserEntityRelationTypes.Other,
                },
              },
            });
          }
        } else {
          const input = { organizationId: activeOrganization.id, ...userData };
          await createAffiliateOrFullUser({
            variables: { input },
            userData,
            updatedAttributes,
          });
        }
      } else {
        if (!userData?.dateOfBirth) delete userData?.dateOfBirth;
        if (!userData?.dateOfDeath) delete userData?.dateOfDeath;
        const cleanedUserData = clearUserObject(userData);
        const input = { userId: affiliateData.user.id, ...cleanedUserData };
        await updateUser({ variables: { input } });
        await updateExistingAccountAffiliations(updatedAttributes);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const validate = () => {
    const invalidFields: typeof focused = [];

    if (requiredFields.includes('fullName') && !affiliateData.user.firstName)
      invalidFields.push('firstName');
    if (requiredFields.includes('fullName') && !affiliateData.user.lastName)
      invalidFields.push('lastName');
    if (
      requiredFields.includes('primaryEmail') &&
      !affiliateData.user.primaryEmail
    )
      invalidFields.push('primaryEmail');
    if (
      requiredFields.includes('phone') &&
      (!affiliateData.user.phone ||
        !phoneValidator(affiliateData.user.phone, countryCode))
    )
      invalidFields.push('phone');
    if (
      requiredFields.includes('physicalAddress') &&
      !affiliateData.user.physicalAddress?.streetName
    )
      invalidFields.push('physicalAddress');
    if (
      requiredFields.includes('dateOfBirth') &&
      (!affiliateData.user.dateOfBirth ||
        dayjs(affiliateData.user.dateOfBirth).isAfter(dayjs()))
    )
      invalidFields.push('dateOfBirth');
    if (
      requiredFields.includes('dateOfDeath') &&
      (!affiliateData.user.dateOfDeath ||
        dayjs(affiliateData.user.dateOfDeath).isAfter(dayjs()))
    )
      invalidFields.push('dateOfDeath');
    if (requiredFields.includes('gender') && !affiliateData.user.gender)
      invalidFields.push('gender');
    if (requiredFields.includes('roleId') && !affiliateData.roleId)
      invalidFields.push('roleId');

    if (requiredFields.includes('relation') && !affiliateData.relation)
      invalidFields.push('relation');
    if (
      (affiliateData.allocation || 0) < 0 ||
      (requiredFields.includes('allocated') &&
        affiliateData.allocation === undefined)
    )
      invalidFields.push('allocated');

    const isTaxIdIncorrect =
      affiliateData.user.taxId && affiliateData.user.taxId.length !== 9;
    if (
      isTaxIdIncorrect ||
      (requiredFields.includes('taxId') &&
        !affiliateData.user.taxIdExists &&
        !affiliateData.user.taxId)
    )
      invalidFields.push('taxId');

    if (allFieldTypes.includes('employmentStatus')) {
      if (
        requiredFields.includes('employmentStatus') &&
        !affiliateData.user.employmentStatus
      )
        invalidFields.push('employmentStatus');

      if (
        EMPLOYED_EMPLOYMENT_STATUSES.includes(
          affiliateData.user.employmentStatus as EmploymentStatus,
        ) &&
        !affiliateData.user.companyType
      )
        invalidFields.push('companyType');
      if (
        EMPLOYED_EMPLOYMENT_STATUSES.includes(
          affiliateData.user.employmentStatus as EmploymentStatus,
        ) &&
        !affiliateData.user.jobTitle
      )
        invalidFields.push('jobTitle');
      if (
        affiliateData.user.employmentStatus === EmploymentStatus.Student &&
        !affiliateData.user.studentAreaOfStudy
      )
        invalidFields.push('studentAreaOfStudy');
    }

    if (
      requiredFields.includes('maritalStatus') &&
      !affiliateData.user.maritalStatus
    )
      invalidFields.push('maritalStatus');
    if (
      requiredFields.includes('citizenships') &&
      !affiliateData.user.citizenships
    )
      invalidFields.push('citizenships');

    if (
      options?.validateAffiliate &&
      isRespBeneficiaryWithEnhancement &&
      [guardianSelector, pcgSelector].some(
        (selector) => !selector.selectedAffiliation,
      )
    ) {
      invalidFields.push('relatedAffiliations');
    }

    setFocused(invalidFields);

    return invalidFields.length === 0;
  };

  const canada = ['CA', 'CANADA'];
  const isOutsideCanadaRegion = affiliateData?.user?.citizenships?.some(
    (x) => !canada.includes((x || '').toUpperCase()),
  );
  const displaySearchWarning =
    existingUserSelected && !isUsingExistingAffiliate && !isUsingAccountOwner;
  const showFields =
    existingUserSelected ||
    grantAction === 'secondary' ||
    !options?.selectExistingUsers ||
    action === 'edit';

  const addressesMatch = (
    addr1: PhysicalAddress | undefined,
    addr2: PhysicalAddress | undefined,
  ): boolean => {
    if (!addr1 || !addr2) return false;
    return isEqual(addr1, addr2);
  };

  const isUsingAccountHoldersAddress = addressesMatch(
    affiliateData.user.physicalAddress,
    account.user?.physicalAddress,
  );

  // get all users that are not affiliateOnly belongs to all of this user's accounts
  const affiliationUsers =
    allAffiliatesForUser
      ?.filter((affiliation: any) => !affiliation.user.affiliateOnly)
      .reduce((unique: any[], affiliation: any) => {
        const exists = unique.some((item) => item.id === affiliation.user.id);
        if (!exists) {
          unique.push({
            id: affiliation.user.id,
            firstName: affiliation.user?.firstName,
            lastName: affiliation.user?.lastName,
            entityName: affiliation.user?.entityName,
            primaryEmail: affiliation.user?.primaryEmail,
          });
        }
        return unique;
      }, []) || [];

  const {
    guardianSelector,
    pcgSelector,
    getRelatedAffiliations,
    isRespBeneficiaryWithEnhancement,
  } = useRespRelatedGuardianAndPcgAffiliations({
    account,
    affiliate,
    affiliationType: type,
    isDisabled,
    show: open && showFields,
    affiliateModalProps: props,
    affiliateSections,
    editAccountSubstepOptions: options,
    showMissingError: focused.includes('relatedAffiliations'),
  });

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant='headingLarge'>
            {(title ?? action === 'create')
              ? t(`button.${type}`)
              : t(`button.edit.${type}`)}
          </Typography>
          <IconButton
            aria-label='close'
            onClick={handleClose}
            disabled={isDisabled}
          >
            <Close fontSize='inherit' />
          </IconButton>
        </Box>
      </DialogTitle>

      <Form onSubmit={submit}>
        <DialogContent>
          {selectExistingUsersEnabled && (
            <Box sx={{ pointerEvents: 'auto', opacity: 1 }}>
              <SegmentedControl
                sx={{ mb: 4 }}
                value={grantAction}
                segments={[
                  { label: t('selectExistingUsers'), value: 'primary' },
                  { label: t('createNewUser'), value: 'secondary' },
                ]}
                disabled={isDisabled}
                exclusive={true}
                fullWidth
                enforceActive
                onChange={(value: SegmentValue) => {
                  setGrantAction(value);
                  if (value === 'secondary') {
                    setDisableEditForm(false);
                  }
                  setExistingUserSelected(false);
                  updateAffiliateData({ ...affiliateData, user: {} });
                }}
              />

              {grantAction === 'primary' && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <ClientSelectField
                    fullWidth
                    autoFocus
                    affiliateLabel
                    disabled={isDisabled}
                    user={undefined}
                    customSearchQuery={SEARCH_CLIENTS_FOR_AFFILIATES}
                    isJointOrAuthorizedIndividual={
                      createOrReturnEntityUsersOnly
                    }
                    label={''}
                    setUser={(selectedUser: User | undefined) => {
                      if (selectedUser) {
                        setExistingUserSelected(true);
                        updateAffiliateData({
                          ...affiliateData,
                          user: selectedUser,
                        });
                      } else {
                        setExistingUserSelected(false);
                      }
                    }}
                    affiliationUsers={affiliationUsers}
                    setDisableEditForm={setDisableEditForm}
                  />
                </Box>
              )}
              {displaySearchWarning && grantAction === 'primary' && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Alert severity='warning'>
                    <Typography variant='bodySmall' component='p'>
                      {t('userSearchWarning')}
                    </Typography>
                  </Alert>
                </Box>
              )}
            </Box>
          )}
          <Box
            sx={{
              pointerEvents: disableEditForm ? 'none' : 'auto',
              opacity: disableEditForm ? 0.5 : 1,
            }}
          >
            {allFieldTypes.includes('fullName') && showFields && (
              <Grid container spacing={2}>
                <Grid size={4}>
                  <TextField
                    disabled={isUsingAccountOwner || isDisabled}
                    testId='affiliate-first-name'
                    label={t('client:details.firstName')}
                    value={affiliateData.user.firstName || ''}
                    onChange={(e: any) =>
                      updateAffiliateData({
                        ...affiliateData,
                        user: {
                          ...affiliateData.user,
                          firstName: e.target.value,
                        },
                      })
                    }
                    onBlur={() => setFocused([...focused, 'firstName'])}
                    error={
                      !affiliateData.user.firstName &&
                      focused.includes('firstName') &&
                      requiredFields.includes('fullName')
                    }
                  />
                </Grid>
                <Grid size={4}>
                  <TextField
                    disabled={isUsingAccountOwner || isDisabled}
                    testId='affiliate-middle-name'
                    label={t('client:details.middleName')}
                    value={affiliateData.user.middleName || ''}
                    onChange={(e: any) =>
                      updateAffiliateData({
                        ...affiliateData,
                        user: {
                          ...affiliateData.user,
                          middleName: e.target.value,
                        },
                      })
                    }
                  />
                </Grid>
                <Grid size={4}>
                  <TextField
                    disabled={isUsingAccountOwner || isDisabled}
                    testId='affiliate-last-name'
                    label={t('client:details.lastName')}
                    value={affiliateData.user.lastName || ''}
                    onChange={(e: any) =>
                      updateAffiliateData({
                        ...affiliateData,
                        user: {
                          ...affiliateData.user,
                          lastName: e.target.value,
                        },
                      })
                    }
                    onBlur={() => setFocused([...focused, 'lastName'])}
                    error={
                      !affiliateData.user.lastName &&
                      focused.includes('lastName') &&
                      requiredFields.includes('fullName')
                    }
                  />
                </Grid>
              </Grid>
            )}
            {allFieldTypes.includes('physicalAddress') && showFields && (
              <>
                <Box
                  sx={{
                    mt: 1,
                  }}
                >
                  <AddressField
                    disabled={isUsingAccountOwner || isDisabled}
                    manualAddressEntry
                    sx={{ mt: 2 }}
                    testId='affiliate-physical-address'
                    address={affiliateData.user.physicalAddress}
                    onChange={(e: any) =>
                      updateAffiliateData({
                        ...affiliateData,
                        user: { ...affiliateData.user, physicalAddress: e },
                      })
                    }
                    label={t('client:details.physicalAddress')}
                    onBlur={() => setFocused([...focused, 'physicalAddress'])}
                    error={
                      !affiliateData.user.physicalAddress?.streetName &&
                      focused.includes('physicalAddress') &&
                      requiredFields.includes('physicalAddress')
                    }
                  />
                </Box>
                {useAccountHoldersAddress && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      mt: 1,
                      flexFlow: 'wrap',
                    }}
                  >
                    <CheckboxField
                      disabled={isUsingAccountOwner || isDisabled}
                      chip
                      data-testid='assign-account-holder-address'
                      label={t('affiliationTypes:assignAccountHoldersAddress')}
                      checked={isUsingAccountHoldersAddress}
                      onChange={(checked: boolean) => {
                        if (checked) {
                          const accountHolderAddress = cloneDeep(
                            account.user?.physicalAddress,
                          ) as
                            | (PhysicalAddress & { __typename?: string })
                            | undefined;
                          if (accountHolderAddress) {
                            delete accountHolderAddress.__typename;
                            updateAffiliateData({
                              ...affiliateData,
                              user: {
                                ...affiliateData.user,
                                physicalAddress: accountHolderAddress,
                              },
                            });
                            return;
                          }
                          updateAffiliateData({
                            ...affiliateData,
                            user: {
                              ...affiliateData.user,
                              physicalAddress: undefined,
                            },
                          });
                        } else {
                          updateAffiliateData({
                            ...affiliateData,
                            user: {
                              ...affiliateData.user,
                              physicalAddress: undefined,
                            },
                          });
                        }
                      }}
                    />
                  </Box>
                )}
              </>
            )}
            {allFieldTypes.includes('dateOfBirth') && showFields && (
              <DateField
                fullWidth
                sx={{ mt: 2 }}
                dataTestId='affiliate-date-of-birth'
                disabled={isDisabled}
                onChange={(date: any) =>
                  updateAffiliateData({
                    ...affiliateData,
                    user: {
                      ...affiliateData.user,
                      dateOfBirth: dayjs(date?.toString()).format('YYYY-MM-DD'),
                    },
                  })
                }
                label={t('client:details.dateOfBirth')}
                value={affiliateData.user.dateOfBirth || ''}
                onBlur={() => setFocused([...focused, 'dateOfBirth'])}
                error={
                  !affiliateData.user.dateOfBirth &&
                  focused.includes('dateOfBirth') &&
                  requiredFields.includes('dateOfBirth')
                }
              />
            )}
            {allFieldTypes.includes('dateOfDeath') && showFields && (
              <DateField
                fullWidth
                sx={{ mt: 2 }}
                dataTestId='affiliate-date-of-death'
                disabled={isDisabled}
                onChange={(date: any) =>
                  updateAffiliateData({
                    ...affiliateData,
                    user: {
                      ...affiliateData.user,
                      dateOfDeath: dayjs(date?.toString()).format('YYYY-MM-DD'),
                    },
                  })
                }
                label={t('client:details.dateOfDeath')}
                value={affiliateData.user.dateOfDeath || ''}
                onBlur={() => setFocused([...focused, 'dateOfDeath'])}
                error={
                  !affiliateData.user.dateOfDeath &&
                  focused.includes('dateOfDeath') &&
                  requiredFields.includes('dateOfDeath')
                }
              />
            )}
            {allFieldTypes.includes('taxId') && showFields && (
              <Box sx={{ mt: 2 }}>
                <TaxIdInputs
                  disabled={isUsingAccountOwner || isDisabled}
                  user={account?.user}
                  affiliateUser={affiliateData.user}
                  setAffiliateUser={(user) =>
                    updateAffiliateData({ ...affiliateData, user })
                  }
                  onBlur={() => {
                    if (
                      !(
                        affiliateData.user.taxIdExists &&
                        !affiliateData.user.taxId
                      )
                    ) {
                      setFocused([...focused, 'taxId']);
                    }
                  }}
                  error={
                    ((affiliateData.user.taxId &&
                      affiliateData.user.taxId.length !== 9) ||
                      (!affiliateData.user.taxId &&
                        requiredFields.includes('taxId'))) &&
                    focused.includes('taxId')
                  }
                />
              </Box>
            )}
            {allFieldTypes.includes('primaryEmail') && showFields && (
              <TextField
                disabled={isUsingAccountOwner || isDisabled}
                fullWidth
                sx={{ mt: 2 }}
                testId='affiliate-primary-email'
                label={t('client:details.primaryEmail')}
                value={affiliateData.user.primaryEmail || ''}
                onChange={(e: any) =>
                  updateAffiliateData({
                    ...affiliateData,
                    user: {
                      ...affiliateData.user,
                      primaryEmail: e.target.value?.trim(),
                    },
                  })
                }
                onBlur={() => setFocused([...focused, 'primaryEmail'])}
                error={
                  !affiliateData.user.primaryEmail &&
                  focused.includes('primaryEmail') &&
                  requiredFields.includes('primaryEmail')
                }
              />
            )}
            {allFieldTypes.includes('phone') && showFields && (
              <PhoneNumberField
                testId='phone'
                disabled={isUsingAccountOwner || isDisabled}
                label={t('client:details.phone')}
                fullWidth
                value={affiliateData.user.phone || ''}
                onBlur={() => setFocused([...focused, 'phone'])}
                error={
                  (!affiliateData.user.phone ||
                    !phoneValidator(affiliateData.user.phone, countryCode)) &&
                  focused.includes('phone') &&
                  requiredFields.includes('phone')
                }
                onChange={(e: any) => {
                  updateAffiliateData({
                    ...affiliateData,
                    user: { ...affiliateData.user, phone: e.value },
                  });
                  setCountryCode(e.countryCode);
                }}
              />
            )}
            {allFieldTypes.includes('gender') && showFields && (
              <Box
                sx={{
                  mt: 2,
                }}
              >
                <Typography
                  variant='labelSmall'
                  component='div'
                  color='textSecondary'
                  sx={{ mb: 1 }}
                >
                  {t('client:details.gender')}
                </Typography>
                <SelectionTile
                  testId='affiliate-gender'
                  direction='row'
                  disabled={isDisabled}
                  value={affiliateData.user.gender || ''}
                  onChange={(e: any) =>
                    updateAffiliateData({
                      ...affiliateData,
                      user: { ...affiliateData.user, gender: e.target.value },
                    })
                  }
                  options={enumValues(Gender).map((g) => ({
                    value: g,
                    label: t(`shared:genderOptions.${g}`),
                  }))}
                  error={
                    !affiliateData.user.gender &&
                    focused.includes('gender') &&
                    requiredFields.includes('gender')
                  }
                />
              </Box>
            )}
            {allFieldTypes.includes('employmentStatus') && showFields && (
              <SelectField
                fullWidth
                sx={{ mt: 2 }}
                disabled={isDisabled}
                testId='affiliate-employment-status'
                label={t('client:details.employmentStatus')}
                value={affiliateData.user.employmentStatus || ''}
                onChange={(e: any) =>
                  updateAffiliateData({
                    ...affiliateData,
                    user: {
                      ...affiliateData.user,
                      employmentStatus: e.target.value,
                    },
                  })
                }
                onBlur={() => setFocused([...focused, 'employmentStatus'])}
                error={
                  !affiliateData.user.employmentStatus &&
                  focused.includes('employmentStatus') &&
                  requiredFields.includes('employmentStatus')
                }
              >
                {[
                  'EMPLOYED',
                  'SELF_EMPLOYED',
                  'UNEMPLOYED',
                  'STUDENT',
                  'RETIRED',
                ].map((value) => (
                  <MenuItem
                    key={value}
                    data-testid={`affiliate-employment-status-${kebabCase(value)}`}
                    value={value}
                  >
                    {t(`client:edit.employmentStatusOptions.${value}`)}
                  </MenuItem>
                ))}
              </SelectField>
            )}
            {allFieldTypes.includes('employmentStatus') &&
              EMPLOYED_EMPLOYMENT_STATUSES.includes(
                affiliateData.user.employmentStatus as EmploymentStatus,
              ) &&
              showFields && (
                <>
                  <TextField
                    fullWidth
                    sx={{ mt: 2 }}
                    disabled={isDisabled}
                    testId='affiliate-company-type'
                    onChange={(e: any) =>
                      updateAffiliateData({
                        ...affiliateData,
                        user: {
                          ...affiliateData.user,
                          companyType: e.target.value,
                        },
                      })
                    }
                    label={t('client:details.companyType')}
                    value={affiliateData.user.companyType}
                    onBlur={() => setFocused([...focused, 'companyType'])}
                    error={
                      !affiliateData.user.companyType &&
                      focused.includes('companyType')
                    }
                  />

                  <TextField
                    fullWidth
                    sx={{ mt: 2 }}
                    disabled={isDisabled}
                    testId='affiliate-job-title'
                    onChange={(e: any) =>
                      updateAffiliateData({
                        ...affiliateData,
                        user: {
                          ...affiliateData.user,
                          jobTitle: e.target.value,
                        },
                      })
                    }
                    label={t('client:details.jobTitle')}
                    value={affiliateData.user.jobTitle}
                    onBlur={() => setFocused([...focused, 'jobTitle'])}
                    error={
                      !affiliateData.user.jobTitle &&
                      focused.includes('jobTitle')
                    }
                  />
                </>
              )}
            {allFieldTypes.includes('employmentStatus') &&
              affiliateData.user.employmentStatus ===
                EmploymentStatus.Student &&
              showFields && (
                <TextField
                  fullWidth
                  sx={{ mt: 2 }}
                  disabled={isDisabled}
                  testId='affiliate-area-of-study'
                  onChange={(e: any) =>
                    updateAffiliateData({
                      ...affiliateData,
                      user: {
                        ...affiliateData.user,
                        studentAreaOfStudy: e.target.value,
                      },
                    })
                  }
                  label={t('client:details.studentAreaOfStudy')}
                  value={affiliateData.user.studentAreaOfStudy}
                  onBlur={() => setFocused([...focused, 'studentAreaOfStudy'])}
                  error={
                    !affiliateData.user.studentAreaOfStudy &&
                    focused.includes('studentAreaOfStudy')
                  }
                />
              )}
            {allFieldTypes.includes('allocated') && showFields && (
              <NumberField
                sx={{ mt: 2 }}
                testId='affiliate-allocated'
                disabled={isDisabled}
                label={t('client:details.allocation')}
                decimalPlaces={2}
                min={0}
                number={(affiliateData.allocation || 0).toString()}
                trailingIcon='percent'
                setNumber={(value: string) =>
                  updateAffiliateData({
                    ...affiliateData,
                    allocation: parseFloat(value) || 0,
                  })
                }
                onBlur={() => setFocused([...focused, 'allocated'])}
                error={
                  ((affiliateData.allocation || 0) < 0 ||
                    (!affiliateData.allocation &&
                      requiredFields.includes('allocated'))) &&
                  focused.includes('allocated')
                }
              />
            )}
            {allFieldTypes.includes('maritalStatus') && showFields && (
              <Grid
                size={12}
                sx={{
                  mt: 2,
                }}
              >
                <SelectField
                  testId='marital-status'
                  onChange={(e: any) =>
                    updateAffiliateData({
                      ...affiliateData,
                      user: {
                        ...affiliateData.user,
                        maritalStatus: e.target.value,
                      },
                    })
                  }
                  disabled={loading || isDisabled}
                  label={t(
                    'client:details.citizenshipAndTaxStatusInformationBox.maritalStatusTitle',
                  )}
                  fullWidth
                  value={affiliateData?.user?.maritalStatus ?? ''}
                  error={
                    !affiliateData?.user?.maritalStatus &&
                    focused.includes('maritalStatus') &&
                    requiredFields.includes('maritalStatus')
                  }
                >
                  <MenuItem data-testid='marital-status-single' value='SINGLE'>
                    {t('client:edit.maritalStatusOptions.SINGLE')}
                  </MenuItem>
                  <MenuItem
                    data-testid='marital-status-married'
                    value='MARRIED'
                  >
                    {t('client:edit.maritalStatusOptions.MARRIED')}
                  </MenuItem>
                  <MenuItem
                    data-testid='marital-status-common-law'
                    value='COMMON_LAW'
                  >
                    {t('client:edit.maritalStatusOptions.COMMON_LAW')}
                  </MenuItem>
                  <MenuItem
                    data-testid='marital-status-divorced'
                    value='DIVORCED'
                  >
                    {t('client:edit.maritalStatusOptions.DIVORCED')}
                  </MenuItem>
                  <MenuItem
                    data-testid='marital-status-separated'
                    value='SEPARATED'
                  >
                    {t('client:edit.maritalStatusOptions.SEPARATED')}
                  </MenuItem>
                  <MenuItem
                    data-testid='marital-status-widowed'
                    value='WIDOWED'
                  >
                    {t('client:edit.maritalStatusOptions.WIDOWED')}
                  </MenuItem>
                  <MenuItem
                    data-testid='marital-status-civil-union'
                    value='CIVIL_UNION'
                  >
                    {t('client:edit.maritalStatusOptions.CIVIL_UNION')}
                  </MenuItem>
                </SelectField>
              </Grid>
            )}
            {allFieldTypes.includes('isOfficerOfPublicCompany') &&
              showFields && (
                <Grid
                  size={{ xs: 12, md: 12 }}
                  sx={{
                    mt: 2,
                  }}
                >
                  <RadioGroup
                    testId={kebabCase('isOfficerOfPublicCompany')}
                    value={
                      affiliateData?.user?.isOfficerOfPublicCompany
                        ? 'yes'
                        : 'no'
                    }
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const newValue = event.target.value === 'yes';
                      updateAffiliateData({
                        ...affiliateData,
                        user: {
                          ...affiliateData.user,
                          isOfficerOfPublicCompany: newValue,
                        },
                      });
                    }}
                    disabled={loading || isDisabled}
                    label={t(
                      'client:details.personAndEntitiesOfInterestBox.officerOfAPublicCompanyTitle',
                    )}
                  >
                    <Radio
                      testId={`${kebabCase('isOfficerOfPublicCompany')}-option-no`}
                      value={'no'}
                      label={t('shared:no')}
                    />

                    <Radio
                      testId={`${kebabCase('isOfficerOfPublicCompany')}-option-yes`}
                      value={'yes'}
                      label={t('shared:yes')}
                    />
                  </RadioGroup>
                </Grid>
              )}
            {allFieldTypes.includes('isOwnerOfPublicCompany') && showFields && (
              <Grid
                size={{ xs: 12, md: 12 }}
                sx={{
                  mt: 2,
                }}
              >
                <RadioGroup
                  testId={kebabCase('isOwnerOfPublicCompany')}
                  value={
                    affiliateData?.user?.isOwnerOfPublicCompany ? 'yes' : 'no'
                  }
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const newValue = event.target.value === 'yes';
                    updateAffiliateData({
                      ...affiliateData,
                      user: {
                        ...affiliateData.user,
                        isOwnerOfPublicCompany: newValue,
                      },
                    });
                  }}
                  disabled={loading || isDisabled}
                  label={t(
                    'client:details.personAndEntitiesOfInterestBox.ownerOfPublicCompanyTitle',
                  )}
                >
                  <Radio
                    testId={`${kebabCase('isOwnerOfPublicCompany')}-option-no`}
                    value={'no'}
                    label={t('shared:no')}
                  />

                  <Radio
                    testId={`${kebabCase('isOwnerOfPublicCompany')}-option-yes`}
                    value={'yes'}
                    label={t('shared:yes')}
                  />
                </RadioGroup>
              </Grid>
            )}
            {allFieldTypes.includes('isMemberOfIiroc') && showFields && (
              <Grid
                size={{ xs: 12, md: 12 }}
                sx={{
                  mt: 2,
                }}
              >
                <RadioGroup
                  testId={kebabCase('isMemberOfIiroc')}
                  value={affiliateData?.user?.isMemberOfIiroc ? 'yes' : 'no'}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const newValue = event.target.value === 'yes';
                    updateAffiliateData({
                      ...affiliateData,
                      user: {
                        ...affiliateData.user,
                        isMemberOfIiroc: newValue,
                      },
                    });
                  }}
                  disabled={loading || isDisabled}
                  label={t(
                    'client:details.personAndEntitiesOfInterestBox.memberOfIIROCTitle',
                  )}
                >
                  <Radio
                    testId={`${kebabCase('isMemberOfIiroc')}-option-no`}
                    value={'no'}
                    label={t('shared:no')}
                  />

                  <Radio
                    testId={`${kebabCase('isMemberOfIiroc')}-option-yes`}
                    value={'yes'}
                    label={t('shared:yes')}
                  />
                </RadioGroup>
              </Grid>
            )}
            {allFieldTypes.includes('politicallyExposedDomesticPerson') &&
              showFields && (
                <Grid
                  size={{ xs: 12, md: 12 }}
                  sx={{
                    mt: 2,
                  }}
                >
                  <RadioGroup
                    testId={kebabCase('politicallyExposedDomesticPerson')}
                    value={
                      affiliateData?.user?.politicallyExposedDomesticPerson
                        ? 'yes'
                        : 'no'
                    }
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const newValue = event.target.value === 'yes';
                      updateAffiliateData({
                        ...affiliateData,
                        user: {
                          ...affiliateData.user,
                          politicallyExposedDomesticPerson: newValue,
                        },
                      });
                    }}
                    disabled={loading || isDisabled}
                    label={t(
                      'client:details.personAndEntitiesOfInterestBox.politicallyExposedDomesticPersonTitle',
                    )}
                  >
                    <Radio
                      testId={`${kebabCase('politicallyExposedDomesticPerson')}-option-no`}
                      value={'no'}
                      label={t('shared:no')}
                    />

                    <Radio
                      testId={`${kebabCase('politicallyExposedDomesticPerson')}-option-yes`}
                      value={'yes'}
                      label={t('shared:yes')}
                    />
                  </RadioGroup>
                </Grid>
              )}
            {allFieldTypes.includes('politicallyExposedForeignPerson') &&
              showFields && (
                <Grid
                  size={{ xs: 12, md: 12 }}
                  sx={{
                    mt: 2,
                  }}
                >
                  <RadioGroup
                    testId={kebabCase('politicallyExposedForeignPerson')}
                    value={
                      affiliateData?.user?.politicallyExposedForeignPerson
                        ? 'yes'
                        : 'no'
                    }
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const newValue = event.target.value === 'yes';
                      updateAffiliateData({
                        ...affiliateData,
                        user: {
                          ...affiliateData.user,
                          politicallyExposedForeignPerson: newValue,
                        },
                      });
                    }}
                    disabled={loading || isDisabled}
                    label={t(
                      'client:details.personAndEntitiesOfInterestBox.politicallyExposedForeignPersonTitle',
                    )}
                  >
                    <Radio
                      testId={`${kebabCase('politicallyExposedForeignPerson')}-option-no`}
                      value={'no'}
                      label={t('shared:no')}
                    />

                    <Radio
                      testId={`${kebabCase('politicallyExposedForeignPerson')}-option-yes`}
                      value={'yes'}
                      label={t('shared:yes')}
                    />
                  </RadioGroup>
                </Grid>
              )}
            {allFieldTypes.includes('closeAssociateOfPEP') && showFields && (
              <Grid
                size={{ xs: 12, md: 12 }}
                sx={{
                  mt: 2,
                }}
              >
                <RadioGroup
                  testId={kebabCase('closeAssociateOfPEP')}
                  value={
                    affiliateData?.user?.closeAssociateOfPEP ? 'yes' : 'no'
                  }
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const newValue = event.target.value === 'yes';
                    updateAffiliateData({
                      ...affiliateData,
                      user: {
                        ...affiliateData.user,
                        closeAssociateOfPEP: newValue,
                      },
                    });
                  }}
                  disabled={loading || isDisabled}
                  label={t(
                    'client:details.personAndEntitiesOfInterestBox.closeAssociateOfPEPTitle',
                  )}
                >
                  <Radio
                    testId={`${kebabCase('closeAssociateOfPEP')}-option-no`}
                    value={'no'}
                    label={t('shared:no')}
                  />

                  <Radio
                    testId={`${kebabCase('closeAssociateOfPEP')}-option-yes`}
                    value={'yes'}
                    label={t('shared:yes')}
                  />
                </RadioGroup>
              </Grid>
            )}
            {allFieldTypes.includes('headOfInternationalOrganization') &&
              showFields && (
                <Grid
                  size={{ xs: 12, md: 12 }}
                  sx={{
                    mt: 2,
                  }}
                >
                  <RadioGroup
                    testId={kebabCase('headOfInternationalOrganization')}
                    value={
                      affiliateData?.user?.headOfInternationalOrganization
                        ? 'yes'
                        : 'no'
                    }
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const newValue = event.target.value === 'yes';
                      updateAffiliateData({
                        ...affiliateData,
                        user: {
                          ...affiliateData.user,
                          headOfInternationalOrganization: newValue,
                        },
                      });
                    }}
                    disabled={loading || isDisabled}
                    label={t(
                      'client:details.personAndEntitiesOfInterestBox.headOfInternationalOrganizationTitle',
                    )}
                  >
                    <Radio
                      testId={`${kebabCase('headOfInternationalOrganization')}-option-no`}
                      value={'no'}
                      label={t('shared:no')}
                    />

                    <Radio
                      testId={`${kebabCase('headOfInternationalOrganization')}-option-yes`}
                      value={'yes'}
                      label={t('shared:yes')}
                    />
                  </RadioGroup>
                </Grid>
              )}
            {allFieldTypes.includes('citizenships') && showFields && (
              <Grid
                size={{ xs: 12, md: 12 }}
                sx={{
                  mt: 2,
                }}
              >
                <CitizenshipField
                  onChange={(e: any) => {
                    updateAffiliateData({
                      ...affiliateData,
                      user: { ...affiliateData.user, citizenships: e },
                    });
                  }}
                  readonly={loading || isDisabled}
                  label={t(
                    'client:details.citizenshipAndTaxStatusInformationBox.citizenshipsTitle',
                  )}
                  citizenships={affiliateData?.user?.citizenships || []}
                  testId='affiliate-citizenships'
                  error={
                    !affiliateData?.user?.citizenships &&
                    focused.includes('citizenships') &&
                    requiredFields.includes('citizenships')
                  }
                />
              </Grid>
            )}
            {allFieldTypes.includes('foreignTaxInformation') &&
              showFields &&
              isOutsideCanadaRegion && (
                <Grid
                  size={{ xs: 12, md: 12 }}
                  sx={{
                    mt: 2,
                  }}
                >
                  <ForeignTaxField
                    setForeignTax={(newValue: ForeignTaxInformation[]) =>
                      updateAffiliateData({
                        ...affiliateData,
                        user: {
                          ...affiliateData.user,
                          foreignTaxInformation: newValue,
                        },
                      })
                    }
                    readonly={loading || isDisabled}
                    label={t(
                      'client:details.citizenshipAndTaxStatusInformationBox.foreignTaxInformationTitle',
                    )}
                    foreignTax={
                      affiliateData?.user?.foreignTaxInformation ?? []
                    }
                    data-testid='affiliate-foreign-tax-information'
                    error={
                      !affiliateData?.user?.foreignTaxInformation &&
                      focused.includes('foreignTaxInformation') &&
                      requiredFields.includes('foreignTaxInformation')
                    }
                  />
                </Grid>
              )}
            {allFieldTypes.includes('relation') && showFields && (
              <SelectField
                fullWidth
                sx={{ mt: 2 }}
                testId='affiliate-relation'
                label={t('client:details.relation')}
                value={affiliateData.relation || ''}
                disabled={isDisabled}
                onChange={(e: any) =>
                  updateAffiliateData({
                    ...affiliateData,
                    relation: e.target.value,
                  })
                }
                onBlur={() => setFocused([...focused, 'relation'])}
                error={
                  !affiliateData.relation &&
                  focused.includes('relation') &&
                  requiredFields.includes('relation')
                }
              >
                {relations
                  ? relations.map((item: any, idx: number) => (
                      <MenuItem key={idx} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))
                  : ALL_RELATIONS.map((item: any, idx: number) => (
                      <MenuItem
                        data-testid={`affiliate-relation-${item}`}
                        key={idx}
                        value={item}
                      >
                        {t(`client:details.relationOptions.${item}`)}
                      </MenuItem>
                    ))}
              </SelectField>
            )}
            {allFieldTypes.includes('roleId') && showFields && (
              <SelectField
                sx={{ mt: 2 }}
                value={affiliateData.roleId}
                label={t('components:accessToEntity.addButton.createTab.role')}
                onChange={(e: any) =>
                  updateAffiliateData({
                    ...affiliateData,
                    roleId: e.target.value,
                  })
                }
                fullWidth
                onBlur={() => setFocused([...focused, 'roleId'])}
                disabled={affiliateData.organizationId === '' || isDisabled}
                error={!affiliateData.roleId && focused.includes('roleId')}
              >
                {rolesLoading ? (
                  <MenuItem>...</MenuItem>
                ) : (
                  data?.fetchRoles?.roles?.map((x: any) => (
                    <MenuItem key={x.id} value={x.id}>
                      {translateBackendString(x.translatedName)} -{' '}
                      {translateBackendString(x.organization.translatedName)}
                    </MenuItem>
                  ))
                )}
              </SelectField>
            )}
            {guardianSelector.jsx}
            {pcgSelector.jsx}
          </Box>
        </DialogContent>
        <DialogFooter>
          {isUsingExistingAffiliate &&
            !isUsingAccountOwner &&
            grantAction === 'secondary' && (
              <Alert severity='warning'>
                <Typography variant='bodySmall' component='p'>
                  {action === 'create'
                    ? t('createWithExistingAffiliation')
                    : t('updateExistingAffiliation')}
                </Typography>
              </Alert>
            )}
          {isUsingAccountOwner && (
            <Alert severity='warning'>
              <Typography variant='bodySmall' component='p'>
                {action === 'create'
                  ? t('createUsingAccountOwner')
                  : t('updateUsingAccountOwner')}
              </Typography>
            </Alert>
          )}
          {disableEditForm && (
            <Alert severity='warning'>
              <Typography variant='bodySmall' component='p'>
                {t('accessDeniedToEditMessage')}
              </Typography>
            </Alert>
          )}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'end',
              p: 1,
            }}
          >
            <Button
              data-testid='create-btn'
              type='submit'
              variant='contained'
              disabled={isDisabled}
              color='secondary'
            >
              {t(`client:form.${action === 'create' ? 'add' : 'update'}`)}
            </Button>
          </Box>
        </DialogFooter>
      </Form>
    </Dialog>
  );
};

export const useRelatedAffiliationSelector =
  createUseRelatedAffiliationSelector(AffiliateModal);

export function useRespRelatedGuardianAndPcgAffiliations(props: {
  account: Partial<Account>;
  affiliate?: Affiliation;
  affiliationType: AffiliationTypes;
  isDisabled: boolean;
  show: boolean;
  affiliateModalProps: AffiliateModalProps;
  affiliateSections: AccountConfigSection[];
  editAccountSubstepOptions?: EditAccountSubstepOptions;
  showMissingError?: boolean;
}) {
  const {
    account,
    affiliate,
    affiliationType,
    isDisabled,
    show,
    affiliateModalProps,
    affiliateSections,
    showMissingError,
  } = props;
  const { t } = useTranslation(['affiliationTypes', 'client']);
  const [fetchAccount] = useFetchAccount(account.id);

  const respEnhancementEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.RespAffiliationsEnhancements,
  );
  const isRespBeneficiaryWithEnhancement =
    isRESPAccount(account?.type) &&
    account?.type !== AccountTypes.RESP_ADULT &&
    affiliationType === AffiliationTypes.PrimaryBeneficiary &&
    respEnhancementEnabled;

  const [updateAffiliations] =
    useMutation<AffiliationUpdateData>(UPDATE_AFFILIATIONS);

  const guardianSelector = useRelatedAffiliationSelector({
    title: t('affiliationTypes:selectionTitle', {
      affiliationType: t(`affiliationTypes:${AffiliationTypes.Guardian}`),
    }),
    relatedAffiliationType: AffiliationTypes.Guardian,
    'data-testid': 'guardian-selector',
    affiliate,
    isDisabled,
    account,
    targetAffiliationType: AffiliationTypes.PrimaryBeneficiary,
    show: show && isRespBeneficiaryWithEnhancement,
    affiliateModalProps,
    affiliateSections,
    showMissingError,
  });

  const pcgSelector = useRelatedAffiliationSelector({
    title: t('affiliationTypes:selectionTitle', {
      affiliationType: t(
        `affiliationTypes:${AffiliationTypes.PrimaryCaregiver}`,
      ),
    }),
    relatedAffiliationType: AffiliationTypes.PrimaryCaregiver,
    'data-testid': 'pcg-selector',
    affiliate,
    isDisabled,
    account,
    targetAffiliationType: AffiliationTypes.PrimaryBeneficiary,
    show: show && isRespBeneficiaryWithEnhancement,
    affiliateModalProps,
    affiliateSections,
    showMissingError,
  });

  const getRelatedAffiliations = async () => {
    if (!isRespBeneficiaryWithEnhancement)
      return { relatedAffiliations: [], incompleteAffiliations: [] };

    const selectors = [guardianSelector, pcgSelector];

    const toAffiliationUpdateInput = (aff?: Partial<Affiliation>) => ({
      id: aff?.id,
      allocation: aff?.allocation,
      relation: aff?.relation,
      type: aff?.type,
      userId: aff?.user?.id,
      relatedAffiliations: aff?.relatedAffiliations || [],
    });

    const { data: backendAccount } = await fetchAccount();
    const existingAffiliationsById = groupBy(
      backendAccount?.fetchAccount?.account?.affiliations || [],
      (aff) => aff.id,
    );

    const { data } = await updateAffiliations({
      variables: {
        input: {
          accountId: account?.id,
          affiliations: [
            ...(account?.affiliations || [])
              .map((aff) =>
                aff.id ? existingAffiliationsById[aff.id]?.at(0) : aff,
              )
              .map(toAffiliationUpdateInput),
            ...selectors
              .filter((s) => !!s.selectedAffiliation)
              .map((s) =>
                s.selectedAffiliation === 'SUBSCRIBER'
                  ? ({
                      type: s.type,
                      userId: account?.user?.id,
                      relation: AffiliationRelations.Other,
                    } as ReturnType<typeof toAffiliationUpdateInput>)
                  : toAffiliationUpdateInput(s.selectedAffiliation),
              )
              .filter(
                ({ id }) =>
                  !account?.affiliations?.some((aff) => aff.id === id),
              ),
          ],
        },
      },
    });

    const relatedAffiliations = selectors
      .filter((s) => !!s.selectedAffiliation)
      .map((s) =>
        s.selectedAffiliation === 'SUBSCRIBER'
          ? data?.updateAffiliations?.account?.affiliations?.find(
              (aff) => aff.user.id === account?.user?.id && s.type === aff.type,
            )
          : s.selectedAffiliation,
      )
      .filter((aff) => !!aff)
      .map(toAffiliationUpdateInput);

    return {
      relatedAffiliations,
      incompleteAffiliations:
        data?.updateAffiliations.incompleteAffiliations || [],
    };
  };

  return {
    isRespBeneficiaryWithEnhancement,
    guardianSelector,
    pcgSelector,
    getRelatedAffiliations,
  };
}
